import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../shared/Button/Button';
import { getEntries } from '../../services/salesApi';
import { StaffSessionError } from '../../services/staffApi';
import { useStaffAuth } from '../../contexts/StaffAuthContext';
import { formatCalendarDate } from '../../utils/dates';
import { EntriesBox } from './sales.styles';

const POLL_MS = 60000;

const sumPair = (a, b) => ({ people: (a.people || 0) + (b.people || 0), coupons: (a.coupons || 0) + (b.coupons || 0) });
const pendingOf = (expected, entered) => ({
  people: Math.max((expected.people || 0) - (entered.people || 0), 0),
  coupons: Math.max((expected.coupons || 0) - (entered.coupons || 0), 0),
});

// Contador de entradas de hoy por actividad (GET /api/empresa/entries).
// `latest` = { activityId, entries, nonce } con el campo `entries` de la última
// respuesta de POST /api/qr/validate: actualiza esa actividad sin esperar otra
// petición. Sirve al dueño y a cualquier staff (el backend limita al staff a
// sus actividades asignadas).
const EntriesPanel = ({ mode, latest }) => {
  const { endSession } = useStaffAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flashId, setFlashId] = useState(null);
  const mounted = useRef(true);
  const dataRef = useRef(null);
  dataRef.current = data;

  // StrictMode (desarrollo) monta, desmonta y vuelve a montar: hay que volver a marcarlo como montado
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const result = await getEntries(mode);
      if (!mounted.current) return;
      setData(result);
      setError('');
    } catch (err) {
      if (!mounted.current) return;
      if (err instanceof StaffSessionError) {
        endSession(err.message);
        return;
      }
      setError(err.message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [mode, endSession]);

  useEffect(() => { load(); }, [load]);

  // Se vuelve a consultar solo si la pestaña está a la vista.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Respuesta de un escaneo: se aplica directo a la actividad correspondiente.
  useEffect(() => {
    if (!latest || !latest.nonce) return;
    if (!latest.entries || latest.activityId == null) {
      load();
      return;
    }
    const known = Boolean(dataRef.current
      && dataRef.current.activities.some((a) => a.activityId === latest.activityId));
    if (!known) {
      // actividad que aún no estaba en la lista: se pide completa
      load();
      return;
    }
    setData((current) => {
      if (!current) return current;
      const activities = current.activities.map((a) => {
        if (a.activityId !== latest.activityId) return a;
        return {
          ...a,
          date: latest.entries.date || a.date,
          entered: latest.entries.entered,
          expected: latest.entries.expected,
          pending: pendingOf(latest.entries.expected, latest.entries.entered),
        };
      });
      const totals = activities.reduce(
        (acc, a) => ({ entered: sumPair(acc.entered, a.entered), expected: sumPair(acc.expected, a.expected) }),
        { entered: { people: 0, coupons: 0 }, expected: { people: 0, coupons: 0 } }
      );
      return { ...current, activities, totals };
    });
    setFlashId(latest.activityId);
    const timer = setTimeout(() => setFlashId(null), 2500);
    return () => clearTimeout(timer);
  }, [latest, load]);

  const activities = (data && data.activities) || [];

  return (
    <EntriesBox aria-label="Entradas de hoy">
      <div className="entries-head">
        <h3>Entradas de hoy{data && data.date ? ` · ${formatCalendarDate(data.date)}` : ''}</h3>
        <Button size="small" variant="secondary" onClick={() => { setLoading(true); load(); }} disabled={loading}>
          {loading ? 'Actualizando…' : 'Actualizar'}
        </Button>
      </div>
      <p className="entries-help">
        Personas = lugares que ya entraron. Cupones = reservas o tickets que ya entraron. Se actualiza con cada escaneo.
      </p>

      {error && <p className="entries-empty" role="alert" style={{ color: 'var(--danger)' }}>{error}</p>}

      {loading && !data ? (
        <p className="entries-empty">Cargando…</p>
      ) : activities.length === 0 && !error ? (
        <p className="entries-empty">No hay actividades para contar. Si eres del equipo, pide que te asignen alguna.</p>
      ) : (
        <ul className="entries-list" aria-live="polite">
          {activities.map((a) => (
            <li key={a.activityId} className={`entries-item${flashId === a.activityId ? ' flash' : ''}`}>
              <div className="entries-title">{a.title}</div>
              <div className="entries-numbers">
                <span><strong>{a.entered.people}</strong> de {a.expected.people} personas</span>
                <span><strong>{a.entered.coupons}</strong> de {a.expected.coupons} cupones</span>
                <span>Faltan {a.pending ? a.pending.people : Math.max(a.expected.people - a.entered.people, 0)} personas</span>
              </div>
            </li>
          ))}
          {activities.length > 1 && data.totals && (
            <li className="entries-item entries-total">
              <div className="entries-title">Total de hoy</div>
              <div className="entries-numbers">
                <span><strong>{data.totals.entered.people}</strong> de {data.totals.expected.people} personas</span>
                <span><strong>{data.totals.entered.coupons}</strong> de {data.totals.expected.coupons} cupones</span>
              </div>
            </li>
          )}
        </ul>
      )}
    </EntriesBox>
  );
};

export default EntriesPanel;
